// Mock data for the CodeMyAI website

// Services data
export const services = [
  {
    id: 'ai-training',
    title: 'AI Training & Education',
    description:
      'Comprehensive training programs to upskill your team on the latest AI technologies and best practices.',
    url: '/services/ai-training',
    iconName: 'education',
    badge: 'Popular',
  },
  {
    id: 'ai-onboarding',
    title: 'Business AI Onboarding',
    description:
      'Seamless integration of AI tools into your existing business processes with minimal disruption.',
    url: '/services/ai-onboarding',
    iconName: 'onboarding',
  },
  {
    id: 'workflow-automation',
    title: 'Workflow Automation',
    description:
      'Create efficient, AI-powered workflows that automate repetitive tasks — including lead intake, CRM sync, and follow-up sequences — freeing up valuable resources.',
    url: '/services/workflow-automation',
    iconName: 'workflow',
  },
  {
    id: 'crm-pipeline-automation',
    title: 'CRM & Pipeline Automation',
    description:
      'Connect your CRM, automate lead intake and follow-up, and build revenue systems that work around the clock — without hiring more staff.',
    url: '/services/crm-pipeline-automation',
    iconName: 'pipeline',
    badge: 'New',
  },
  {
    id: 'custom-ai-development',
    title: 'Custom AI Development',
    description:
      'Tailor-made AI solutions designed specifically for your unique business challenges and opportunities.',
    url: '/services/custom-ai-development',
    iconName: 'development',
    badge: 'Advanced',
  },
  {
    id: 'ai-optimization',
    title: 'AI System Optimization',
    description:
      'Fine-tune your existing AI implementations to maximize performance, accuracy, and ROI.',
    url: '/services/ai-optimization',
    iconName: 'optimization',
  },
  {
    id: 'web-design',
    title: 'Web Design & Development',
    description:
      'AI-enhanced responsive websites and web applications that convert visitors into customers with smart UX optimization.',
    url: '/services/web-design',
    iconName: 'design',
  },
  {
    id: 'software-development',
    title: 'Custom Software Development',
    description:
      'Bespoke software solutions with AI capabilities, built to scale with your business and integrate seamlessly.',
    url: '/services/software-development',
    iconName: 'code',
  },
  {
    id: 'seo',
    title: 'SEO Services',
    description:
      'AI-driven search engine optimization that improves rankings, drives organic traffic, and increases conversions.',
    url: '/services/seo',
    iconName: 'search',
  },
  {
    id: 'digital-marketing',
    title: 'Digital Marketing',
    description:
      'Data-driven marketing campaigns with AI insights for maximum ROI and sustainable business growth.',
    url: '/services/digital-marketing',
    iconName: 'marketing',
  },
];

// Testimonials data
export const testimonials = [
  {
    id: '1',
    quote:
      "CodeMyAI transformed our customer service operations through intelligent automation. Their training program was exceptional—our team now confidently manages our AI tools, and we've seen a 40% reduction in response times.",
    author: 'Sarah Johnson',
    title: 'Chief Customer Officer',
    company: 'NexTech Solutions',
    rating: 5,
  },
  {
    id: '2',
    quote:
      'As a small business owner, I was intimidated by AI. CodeMyAI made the onboarding process incredibly simple. Their step-by-step approach helped us implement AI-driven analytics that increased our conversion rates by 28%.',
    author: 'Michael Chen',
    title: 'Founder',
    company: 'Artisan Ecommerce',
    rating: 5,
  },
  {
    id: '3',
    quote:
      "The custom AI agent CodeMyAI developed for our healthcare practice has revolutionized our patient scheduling system. It's reduced administrative work by 60% and patients love the seamless experience.",
    author: 'Dr. Alicia Martinez',
    title: 'Medical Director',
    company: 'Integrated Health Partners',
    rating: 5,
  },
  {
    id: '4',
    quote:
      "CodeMyAI's workflow automation solution eliminated our data entry bottlenecks. What used to take our team days now happens automatically with greater accuracy. The ROI was evident within the first month.",
    author: 'Robert Kang',
    title: 'Operations Manager',
    company: 'Logistics International',
    rating: 4,
  },
  {
    id: '5',
    quote:
      'The AI optimization service from CodeMyAI fine-tuned our recommendation engine to deliver truly personalized results. Our customer engagement metrics have improved dramatically as a result.',
    author: 'Jessica Torres',
    title: 'Digital Marketing Director',
    company: 'Consumer Brands Inc.',
    rating: 5,
  },
];

// Case studies data
export const caseStudies = [
  {
    id: 'case-study-1',
    title: 'How RetailCorp Increased Sales by 32% with AI-Driven Customer Insights',
    excerpt:
      'Learn how we helped a national retail chain leverage customer data to create personalized shopping experiences that dramatically increased conversion rates.',
    industry: 'Retail',
    solution: 'Custom AI Development',
    results: [
      '32% increase in sales',
      '47% improvement in customer satisfaction',
      '3.5x ROI within 6 months',
    ],
    imageSrc: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=800&h=400&fit=crop',
    url: '/case-studies/retailcorp-customer-insights',
  },
  {
    id: 'case-study-2',
    title: 'Financial Services Firm Reduces Document Processing Time by 85%',
    excerpt:
      'See how we automated document processing for a financial services provider, cutting processing times from days to minutes while improving accuracy.',
    industry: 'Financial Services',
    solution: 'Workflow Automation',
    results: [
      '85% reduction in processing time',
      '93% accuracy improvement',
      '1.2M annual cost savings',
    ],
    imageSrc: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop',
    url: '/case-studies/financial-document-automation',
  },
  {
    id: 'case-study-3',
    title: 'Manufacturing Company Predicts Equipment Failures Before They Happen',
    excerpt:
      'Discover how our predictive maintenance solution helped a manufacturing company avoid costly downtime by anticipating equipment failures before they occurred.',
    industry: 'Manufacturing',
    solution: 'AI System Optimization',
    results: [
      '76% reduction in unplanned downtime',
      '43% decrease in maintenance costs',
      '2.8x ROI within first year',
    ],
    imageSrc: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800&h=400&fit=crop',
    url: '/case-studies/manufacturing-predictive-maintenance',
  },
  {
    id: 'case-study-4',
    title: 'B2B Consultancy Triples Pipeline Conversion with AI Follow-Up Automation',
    excerpt:
      'A UK-based B2B services firm automated their lead intake and follow-up, cutting response time from 2 days to 4 minutes and tripling pipeline conversion rates — with zero additional headcount.',
    industry: 'Professional Services',
    solution: 'CRM & Pipeline Automation',
    results: [
      '3x pipeline conversion rate',
      'Response time reduced from 2 days to 4 minutes',
      'Zero additional headcount required',
    ],
    imageSrc: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop',
    url: '/case-studies/b2b-pipeline-automation',
  },
];

// Blog posts data
export const blogPosts = [
  {
    id: 'blog-1',
    title: "The Business Leader's Guide to Implementing AI in 2025",
    excerpt:
      'A comprehensive roadmap for business leaders looking to successfully integrate AI into their operations in 2025 and beyond.',
    category: 'Strategy',
    author: 'Dr. Marcus Wei',
    publishDate: 'April 15, 2025',
    readTime: '8 min read',
    imageSrc: '/assets/images/blog/ai-implementation-guide.jpg',
    url: '/blog/business-leaders-ai-guide-2025',
  },
  {
    id: 'blog-2',
    title: '5 AI Automation Workflows That Save Small Businesses 20+ Hours Weekly',
    excerpt:
      'Practical automation workflows that small businesses can implement today to reclaim valuable time and boost productivity.',
    category: 'Automation',
    author: 'Sophia Rodriguez',
    publishDate: 'April 8, 2025',
    readTime: '6 min read',
    imageSrc: '/assets/images/blog/ai-automation-workflows.jpg',
    url: '/blog/ai-automation-workflows-small-business',
  },
  {
    id: 'blog-3',
    title: 'How to Train Your Team on AI Tools: A Step-by-Step Approach',
    excerpt:
      'Best practices for effectively upskilling your team on new AI tools to ensure high adoption rates and maximize ROI.',
    category: 'Training',
    author: 'James Anderson',
    publishDate: 'March 27, 2025',
    readTime: '10 min read',
    imageSrc: '/assets/images/blog/ai-team-training.jpg',
    url: '/blog/train-team-ai-tools-approach',
  },
  {
    id: 'blog-4',
    title: 'How B2B Service Businesses Are Using AI to Automate Their Sales Pipeline',
    excerpt:
      'A look at how professional services firms are deploying AI-powered intake and follow-up automation to close more deals without growing their team.',
    category: 'Automation',
    author: 'Sophia Rodriguez',
    publishDate: 'April 1, 2025',
    readTime: '7 min read',
    imageSrc: '/assets/images/blog/b2b-pipeline-ai.jpg',
    url: '/blog/b2b-ai-sales-pipeline-automation',
  },
  {
    id: 'blog-5',
    title: 'CRM Automation in 2025: Turn Follow-Ups Into Closed Deals Without More Staff',
    excerpt:
      'How AI-driven CRM automation is helping B2B businesses respond to leads in minutes, not days, and dramatically increasing conversion rates.',
    category: 'Growth',
    author: 'Dr. Marcus Wei',
    publishDate: 'March 18, 2025',
    readTime: '8 min read',
    imageSrc: '/assets/images/blog/crm-automation-2025.jpg',
    url: '/blog/crm-automation-follow-ups-2025',
  },
];

// FAQ data
export const faqs = [
  {
    question: 'What level of technical expertise do we need to implement AI in our business?',
    answer:
      "You don't need advanced technical expertise to start implementing AI. Our approach focuses on making AI accessible regardless of your current technical capabilities. We provide comprehensive training and support to ensure your team can confidently use and manage the AI solutions we implement.",
  },
  {
    question: 'How long does the AI implementation process typically take?',
    answer:
      "Implementation timelines vary based on the complexity of your needs and the scope of the project. Simple workflow automations can be up and running in 1-2 weeks, while more comprehensive AI transformations might take 1-3 months. During our initial consultation, we'll provide a detailed timeline specific to your project.",
  },
  {
    question: 'What kind of ROI can we expect from implementing AI solutions?',
    answer:
      'Our clients typically see ROI in three main areas: time savings (reduction in manual work hours), cost reduction (lower operational expenses), and revenue growth (improved customer experiences and decision-making). Most clients achieve positive ROI within 3-6 months, with our average client seeing a 3-5x return on their investment within the first year.',
  },
  {
    question: 'Do you offer ongoing support after implementation?',
    answer:
      "Yes, we provide comprehensive ongoing support through our managed service plans. These include regular performance reviews, optimization adjustments, troubleshooting assistance, and continued training as AI technologies evolve. We're committed to being your long-term partner in AI success.",
  },
  {
    question: 'How do you ensure the security and privacy of our data?',
    answer:
      "Data security and privacy are foundational to our approach. We implement industry-leading security practices, comply with relevant regulations (GDPR, CCPA, etc.), and take a privacy-by-design approach to all implementations. We'll work with your IT and legal teams to ensure all solutions meet your security requirements and regulatory obligations.",
  },
  {
    question: 'Which CRMs do you integrate with?',
    answer:
      'We integrate with all major CRM platforms including HubSpot, Salesforce, Pipedrive, and Zoho CRM. We also support custom integrations through Zapier, Make (formerly Integromat), and direct API connections. During onboarding we assess your existing stack and recommend the best integration path.',
  },
  {
    question: 'Can you automate our lead follow-up without replacing our sales team?',
    answer:
      "Absolutely — and that's exactly the goal. Our automation handles speed-to-lead responses, nurture sequences, and pipeline updates so your sales team can focus entirely on closing. Think of it as giving every rep an AI assistant that never sleeps. Your team stays in control; the AI eliminates the repetitive work.",
  },
];

// Target audience data
export const targetAudiences = [
  {
    id: 'individuals',
    title: 'For Individuals',
    description:
      'Build your AI skills and supercharge your career through our specialized training programs designed for professionals looking to stay ahead of the curve.',
    features: [
      'Self-paced AI literacy courses',
      'Role-specific AI certification programs',
      'Hands-on workshops and labs',
      'One-on-one AI coaching sessions',
      'Career advancement roadmaps',
    ],
    iconName: 'person',
    ctaText: 'Explore Individual Solutions',
    ctaLink: '/solutions/individuals',
  },
  {
    id: 'small-business',
    title: 'For Small Businesses',
    description:
      'Level the playing field with enterprise-grade AI solutions scaled to fit your small business needs and budget.',
    features: [
      'Cost-effective AI implementation',
      'Small business automation packages',
      'AI-powered lead follow-up automation',
      'CRM integration & pipeline management',
      'Ongoing technical support',
    ],
    iconName: 'building-store',
    ctaText: 'Discover Small Business Solutions',
    ctaLink: '/solutions/small-business',
  },
  {
    id: 'startups',
    title: 'For Startups',
    description:
      'Leverage AI to accelerate growth, optimize limited resources, and create scalable products that stand out in competitive markets.',
    features: [
      'AI product development assistance',
      'Startup-friendly pricing models',
      'Pipeline automation from day one',
      'Growth system setup & CRM integration',
      'Investor-ready AI roadmaps',
    ],
    iconName: 'rocket',
    ctaText: 'View Startup Solutions',
    ctaLink: '/solutions/startups',
  },
  {
    id: 'enterprise',
    title: 'For Enterprise',
    description:
      'Transform your organization with comprehensive AI solutions that drive efficiency, innovation, and competitive advantage at scale.',
    features: [
      'Enterprise-wide AI strategy',
      'Legacy system AI integration',
      'Custom AI solution development',
      'Department-specific optimization',
      'Employee AI upskilling programs',
    ],
    iconName: 'building-skyscraper',
    ctaText: 'Explore Enterprise Solutions',
    ctaLink: '/solutions/enterprise',
  },
];

// Industry solutions data
export const industrySolutions = [
  {
    id: 'healthcare',
    title: 'Healthcare',
    description:
      'AI solutions that improve patient care, optimize administrative workflows, and enhance clinical decision-making.',
    useCases: [
      'Patient scheduling optimization',
      'Medical record analysis and insights',
      'Predictive care management',
      'Administrative task automation',
      'Healthcare compliance monitoring',
    ],
    iconName: 'heartbeat',
    ctaLink: '/industries/healthcare',
  },
  {
    id: 'finance',
    title: 'Finance',
    description:
      'Secure, compliant AI implementations that enhance customer experiences and streamline financial operations.',
    useCases: [
      'Fraud detection and prevention',
      'Automated document processing',
      'Customer service AI assistants',
      'Risk assessment optimization',
      'Personalized financial recommendations',
    ],
    iconName: 'currency-dollar',
    ctaLink: '/industries/finance',
  },
  {
    id: 'retail',
    title: 'Retail',
    description:
      'Transform your retail business with AI-driven customer insights, inventory management, and personalized shopping experiences.',
    useCases: [
      'Customer behavior analysis',
      'Inventory optimization',
      'Dynamic pricing strategies',
      'Personalized marketing campaigns',
      'Supply chain management',
    ],
    iconName: 'shopping-bag',
    ctaLink: '/industries/retail',
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing',
    description:
      'Optimize production processes, predict maintenance needs, and reduce costs with industrial AI solutions.',
    useCases: [
      'Predictive maintenance',
      'Quality control automation',
      'Production line optimization',
      'Supply chain intelligence',
      'Energy usage optimization',
    ],
    iconName: 'factory',
    ctaLink: '/industries/manufacturing',
  },
  {
    id: 'education',
    title: 'Education',
    description:
      'Enhance learning outcomes and institutional efficiency with AI solutions designed for educational environments.',
    useCases: [
      'Personalized learning paths',
      'Automated grading systems',
      'Student engagement analysis',
      'Administrative process automation',
      'Learning resource optimization',
    ],
    iconName: 'book-open',
    ctaLink: '/industries/education',
  },
  {
    id: 'legal',
    title: 'Legal',
    description:
      'Streamline legal processes, enhance research capabilities, and improve client services with targeted AI solutions.',
    useCases: [
      'Document review and analysis',
      'Legal research assistant',
      'Contract management automation',
      'Billing optimization',
      'Case outcome prediction',
    ],
    iconName: 'scale',
    ctaLink: '/industries/legal',
  },
];

// Stats data
export const stats = [
  {
    label: 'Clients Empowered',
    value: '10+',
  },
  {
    label: 'Years of Combined Experience',
    value: '25+',
  },
  {
    label: 'AI Solutions Deployed',
    value: '4+',
  },
  {
    label: 'Revenue Generated for Clients',
    value: '£1M+',
  },
  {
    label: 'Average Pipeline Growth',
    value: '3x',
  },
];

// Team members data
export const teamMembers = [
  {
    id: 'member-1',
    name: 'Kumar Praneet',
    title: 'Founder & CTO',
    bio: 'AI trailblazer, 20+ years of experience in web and software development. Passionate about making AI accessible to businesses of all sizes.',
    imageSrc: '/assets/images/team/praneet-srivastav.jpg',
  },
  {
    id: 'member-2',
    name: 'Prashant Srivastav',
    title: 'Chief Planning Officer',
    bio: 'AI/software systems architect with 15+ years of experience implementing complex business critical solutions across various industries.',
    imageSrc: '/assets/images/team/prashant-srivastav.jpg',
  },
  {
    id: 'member-3',
    name: 'Amit Ranjan',
    title: 'Head of Operations',
    bio: 'Senior AI strategist with 20+ years of experience in developing and deploying AI solutions for complex business problems.',
    imageSrc: '/assets/images/team/amit-ranjan.jpg',
  },
  {
    id: 'member-4',
    name: 'Noopur Srivastava',
    title: 'Head of Quality',
    bio: 'Quality assurance expert with 12+ years of experience ensuring the reliability and performance of systems in production environments.',
    imageSrc: '/assets/images/team/noopur-srivastava.jpg',
  },
];

// Partners data
export const partners = [
  {
    name: 'OpenAI',
    logoSrc: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/OpenAI_Logo.svg/1200px-OpenAI_Logo.svg.png',
  },
  {
    name: 'Anthropic',
    logoSrc: '/assets/images/partners/anthropic.svg',
  },
  {
    name: 'Google Gemini',
    logoSrc: 'https://upload.wikimedia.org/wikipedia/commons/d/d9/Google_Gemini_logo_2025.svg',
  },
  {
    name: 'Microsoft',
    logoSrc: '/assets/images/partners/microsoft.svg',
  },
  {
    name: 'Google Cloud',
    logoSrc: '/assets/images/partners/googlecloud.png',
  },
  {
    name: 'Supabase',
    logoSrc: '/assets/images/partners/supabase.svg',
  },
  {
    name: 'AWS',
    logoSrc: '/assets/images/partners/aws.svg',
  },
];

// Free resources data
export const freeResources = [
  {
    id: 'resource-1',
    title: 'AI Readiness Assessment Tool',
    description:
      "Evaluate your organization's ability to successfully implement and benefit from AI technologies.",
    type: 'Interactive Tool',
    imageSrc: '/assets/images/resources/ai-readiness.jpg',
    url: '/resources/ai-readiness-assessment',
  },
  {
    id: 'resource-2',
    title: 'The Complete Guide to AI Implementation',
    description:
      'A comprehensive ebook covering everything you need to know about successfully bringing AI into your organization.',
    type: 'Ebook (PDF)',
    imageSrc: '/assets/images/resources/ai-implementation-guide.jpg',
    url: '/resources/ai-implementation-guide',
  },
  {
    id: 'resource-3',
    title: 'AI ROI Calculator',
    description:
      'Calculate the potential return on investment from implementing various AI solutions in your business.',
    type: 'Calculator',
    imageSrc: '/assets/images/resources/ai-roi-calculator.jpg',
    url: '/resources/ai-roi-calculator',
  },
];

// Pricing data
export const pricingPlans = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for small businesses just beginning their AI journey',
    price: 500,
    billing: 'one-time setup + $50/month',
    features: [
      'AI readiness assessment',
      'Basic workflow automation (up to 3 workflows)',
      'Team training workshop (4 hours)',
      'Standard support (business hours)',
      'Monthly performance review',
    ],
    mostPopular: false,
    ctaText: 'Get Started',
    ctaLink: '/pricing/starter',
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Comprehensive AI solutions for growing businesses',
    price: 2995,
    billing: 'one-time setup + $100/month',
    features: [
      'Everything in Starter, plus:',
      'Advanced workflow automation (up to 10 workflows)',
      'Custom AI assistant development',
      'Team training program (16 hours)',
      'Priority support (extended hours)',
      'Bi-weekly performance optimization',
      'Dedicated success manager',
    ],
    mostPopular: true,
    ctaText: 'Choose Professional',
    ctaLink: '/pricing/professional',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Tailored solutions for large organizations with complex needs',
    price: null,
    billing: 'Custom pricing',
    features: [
      'Everything in Professional, plus:',
      'Unlimited workflow automation',
      'Custom AI solution development',
      'Enterprise-wide training program',
      '24/7 premium support',
      'Weekly performance optimization',
      'Dedicated technical account manager',
      'Custom integration development',
      'On-site implementation support',
    ],
    mostPopular: false,
    ctaText: 'Contact Sales',
    ctaLink: '/contact?inquiry=enterprise',
  },
];

// Contact information
export const contactInfo = {
  email: 'Hello@CodeMyAI.com',
  phone: '+44 07415928896',
  address: '31 Giles Drive, London, UK',
  hours: 'Monday - Friday: 9:00 AM - 6:00 PM GMT',
  socialMedia: {
    twitter: 'https://twitter.com/CodeMyAI',
    linkedin: 'https://linkedin.com/company/CodeMyAI',
  },
};
